import { FC, useEffect, useState } from "react";
import { FetchState, useAll } from "@hulanbv/nest-utilities-client-state";
import { ActivityType, IActivityEntry } from "@hulanbv/platformapp";
import { stylesheet } from "typestyle";
import { PageBody } from "../../../elements/page-body.element";
import { Page } from "../../../elements/page.element";
import { activityComponentsService } from "../../../../state/activities/activity-components.service";
import { LoadingSpinner } from "../../../elements/loading-spinner.element";
import { ValueIdentificationSwipeView } from "./value-identification-swipe-view";
import { ValueIdentificationTrimView } from "./value-identification-trim-view";
import { Button } from "../../../elements/button.element";
import { Flex } from "../../../elements/flex.element";
import { activityEntriesService } from "../../../../state/activities/activity-entries.service";
import { authenticationService } from "../../../../state/authentication/authentication.service";
import { ValueIdentificationOverviewView } from "./value-identification-overview-view";
import { useInviteInteractionFromQuery } from "../../../../state/exercise-invite-interactions/use-invite-interaction-from-query.hook";

const exerciseTitle = "Values Card Sort";

const ValueIdentificationActivityScreen: FC = () => {
  const exerciseInviteId = useInviteInteractionFromQuery();
  const [activityState, setActivityState] = useState<
    "informing" | "playing" | "saving" | "completed"
  >(!exerciseInviteId ? "informing" : "playing");
  const [keptValues, setKeptValues] = useState<string[] | null>(null);
  const { data: activityComponents, fetchState } = useAll(
    activityComponentsService,
  );

  useEffect(() => {
    // always reset the scroll when the activity state changes
    window.scrollTo(0, 0);

    async function saveValues() {
      if (keptValues !== null && exerciseInviteId) {
        await activityEntriesService.postMany({
          payload: keptValues.map(
            (value) =>
              ({
                value,
                activityType: ActivityType.VALUE_IDENTIFICATION,
                exerciseInviteId,
                userId: authenticationService.getSession()?.userId ?? "",
              } as IActivityEntry),
          ),
        });
      }

      setActivityState("completed");
    }

    // save the selected values when exactly 5 values have been kept
    if (keptValues?.length === 5 && activityState === "playing") {
      setActivityState("saving");
      saveValues();
    }
  }, [activityState, exerciseInviteId, keptValues]);

  if (
    fetchState === FetchState.Pending ||
    !activityComponents ||
    activityState === "saving"
  ) {
    return (
      <Page hideGoBack hideProfile title={exerciseTitle} isNavigationDisabled>
        <PageBody>
          <Flex flex={1} justifyContent="center" alignItems="center">
            <LoadingSpinner color="black" />
          </Flex>
        </PageBody>
      </Page>
    );
  }

  if (activityState === "informing") {
    return (
      <Page
        hideGoBack
        hideProfile
        title={exerciseTitle}
        isNavigationDisabled
        className={styles.page}
      >
        <PageBody fullHeight>
          <Flex flex={1} justifyContent="center" alignItems="center">
            <p style={{ textAlign: "center" }}>
              The exercise was not opened through an exercise invite. You can
              continue the exercise but the results will not be saved.
            </p>
          </Flex>
          <Button
            centerChildren
            attributes={{ onClick: () => setActivityState("playing") }}
          >
            I understand
          </Button>
        </PageBody>
      </Page>
    );
  }

  if (keptValues !== null && keptValues.length < 5) {
    return (
      <Page
        hideGoBack
        hideProfile
        title={exerciseTitle}
        isNavigationDisabled
        className={styles.page}
      >
        <PageBody fullHeight>
          <Flex
            flex={1}
            justifyContent="center"
            alignItems="center"
            direction="column"
          >
            <h3 style={{ textAlign: "center", margin: 0 }}>
              You Need More Values
            </h3>
            <p style={{ textAlign: "center", margin: 0 }}>
              You need to select at least 5 values to proceed. Please go back
              and select more values.
            </p>
          </Flex>

          <Button
            centerChildren
            attributes={{ onClick: () => setKeptValues(null) }}
          >
            Start over
          </Button>
        </PageBody>
      </Page>
    );
  }

  if (activityState === "completed") {
    return (
      <Page
        key={keptValues?.length}
        hideGoBack
        hideProfile
        title={exerciseTitle}
        isNavigationDisabled
        className={styles.page}
      >
        <PageBody fullHeight>
          <ValueIdentificationOverviewView values={keptValues ?? []} />
        </PageBody>
      </Page>
    );
  }

  if (keptValues !== null && keptValues.length > 5) {
    // all cards processed, show results
    return (
      <Page
        hideGoBack
        hideProfile
        title={exerciseTitle}
        isNavigationDisabled
        className={styles.page}
      >
        <PageBody fullHeight>
          <ValueIdentificationTrimView
            key={keptValues.length}
            values={keptValues}
            onConfirm={setKeptValues}
          />
        </PageBody>
      </Page>
    );
  }

  return (
    <Page
      hideGoBack
      hideProfile
      title={exerciseTitle}
      isNavigationDisabled
      className={styles.page}
    >
      <PageBody fullHeight>
        <ValueIdentificationSwipeView
          onComplete={setKeptValues}
          activityComponents={activityComponents}
        />
      </PageBody>
    </Page>
  );
};

export { ValueIdentificationActivityScreen };

const styles = stylesheet({
  page: {
    background: "#fafafa",
    overflow: "hidden",
  },
});
